import type { ReactNode } from "react";
import { useCurrentOrganisation } from "../root/CurrentOrganisationProvider";

export type Permission =
  /** Registrations */
  | "nova-forms-api:registration:read"
  | "nova-forms-api:registration:create"
  | "nova-forms-api:registration:delete"
  | "nova-forms-api:registration:update"
  | "nova-forms-api:registration:invite"
  | "nova-forms-api:registration:answers:update"
  | "nova-forms-api:registration:review"
  | "nova-forms-api:enterprise-customers:registrations:manage"
  | "nova-forms-api:enterprise-customers:option-categories:manage"

  /** Dashboard */
  | "nova-forms-api:dashboard:read"

  /** Systems */
  | "nova-forms-api:system:read"
  | "nova-forms-api:system:create"
  | "nova-forms-api:system:delete"
  | "nova-forms-api:system:update"
  | "nova-forms-api:system:gridview"
  | "nova-forms-api:system:select-options"

  /** Organization */
  | "organization-api:tenant:read"
  | "organization-api:tenant:hierarchy:read"
  | "organization-api:tenant:hierarchy:update"
  | "organization-api:tenant:hierarchy:list"
  | "organization-api:user:read"
  | "organization-api:user:delete"
  | "organization-api:user:create"
  | "organization-api:user:roles:read"
  | "organization-api:user:roles:update:customerLevel"
  | "organization-api:user:roles:update:productLevel"
  | "organization-api:scim:read"

  /** Option Categories */
  | "nova-forms-api:category-options:categories-of-personal-data:create"
  | "nova-forms-api:category-options:categories-of-personal-data:delete"
  | "nova-forms-api:category-options:categories-of-personal-data:gridview"
  | "nova-forms-api:category-options:categories-of-personal-data:update"
  | "nova-forms-api:category-options:categories-of-data-subjects:create"
  | "nova-forms-api:category-options:categories-of-data-subjects:delete"
  | "nova-forms-api:category-options:categories-of-data-subjects:gridview"
  | "nova-forms-api:category-options:categories-of-data-subjects:update"
  | "nova-forms-api:category-options:technical-security-measurements:create"
  | "nova-forms-api:category-options:technical-security-measurements:delete"
  | "nova-forms-api:category-options:technical-security-measurements:gridview"
  | "nova-forms-api:category-options:technical-security-measurements:update"

  /** Option Category */
  | "nova-forms-api:category-options:projects:create"
  | "nova-forms-api:category-options:projects:delete"
  | "nova-forms-api:category-options:projects:gridview"
  | "nova-forms-api:category-options:projects:update"
  | "nova-forms-api:category-options:categories-of-processings:create"
  | "nova-forms-api:category-options:categories-of-processings:delete"
  | "nova-forms-api:category-options:categories-of-processings:gridview"
  | "nova-forms-api:category-options:categories-of-processings:update"

  /** Personal Data Controllers */
  | "nova-forms-api:pdc:settings"

  /** Form template settings */
  | "nova-forms-api:organisation:form:settings:read"
  | "nova-forms-api:organisation:form:settings:update"

  /** External Organisations */
  | "nova-forms-api:external-organisation:create"
  | "nova-forms-api:external-organisation:read"
  | "nova-forms-api:external-organisation:update"
  | "nova-forms-api:external-organisation:delete"

  /** News */
  | "organization-api:news:read"

  /** DPIA */
  | "nova-forms-api:dpia:create"
  | "nova-forms-api:dpia:gridview"
  | "nova-forms-api:dpia:list"
  | "nova-forms-api:dpia:delete"
  | "nova-forms-api:dpia:invite"
  | "nova-forms-api:dpia:read"
  | "nova-forms-api:dpia:update"

  /** Data import */
  | "nova-forms-api:data-import"

  /** Measure */
  | "nova-forms-api:measure:read"
  | "nova-forms-api:measure:create"
  | "nova-forms-api:measure:delete"
  | "nova-forms-api:measure:update"
  | "nova-forms-api:measure:gridview"
  | "nova-forms-api:measure:list"

  /** Partners */
  | "nova-forms-api:partner:create"
  | "nova-forms-api:partner:read"
  | "nova-forms-api:partner:update"
  | "nova-forms-api:partner:delete"
  | "nova-forms-api:partner:gridview"
  | "nova-forms-api:partner:select-options"

  /** Information Types */
  | "nova-forms-api:information-type:create"
  | "nova-forms-api:information-type:read"
  | "nova-forms-api:information-type:update"
  | "nova-forms-api:information-type:delete"
  | "nova-forms-api:information-type:gridview"
  | "nova-forms-api:information-type:select-options";

export const usePermission = () => {
  const currentOrganisation = useCurrentOrganisation();

  /** If no permissions are passed in, the permissions on the current organisation is used.
   * The override can be used when the permissions on a certain tenant is needed */
  const hasPermission = (requiredPermission: Permission, permissionsOverride?: readonly Permission[]) => {
    return (permissionsOverride ?? currentOrganisation.permissions).includes(requiredPermission);
  };

  return { hasPermission };
};

export const HasPermission = ({ permission, children }: { permission: Permission; children: ReactNode }) => {
  const { hasPermission } = usePermission();
  return hasPermission(permission) ? children : null;
};
